import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { dismissUpdateModal, clearUpdateNotification } from "@common-reducers/UpdateNotificationReducer";
import { ArrowUpCircle, RefreshCw, AlertCircle } from "lucide-react";
import { useState } from "react";

interface UpdateNotificationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Update Notification Modal Component
 * 
 * Modal dialog that appears when an extension update is detected.
 * Provides options to update now or dismiss and update later.
 */
export default function UpdateNotificationModal({
  open,
  onOpenChange,
}: UpdateNotificationModalProps) {
  const dispatch = useAppDispatch();
  const [isUpdating, setIsUpdating] = useState(false);

  const currentVersion = useAppSelector(
    (state) => state.UpdateNotificationReducer?.currentVersion
  );
  const newVersion = useAppSelector(
    (state) => state.UpdateNotificationReducer?.newVersion
  );

  const handleUpdateNow = () => {
    setIsUpdating(true);

    // Refreshing the page always recovers: it re-injects the (new) content
    // script and re-reads update state from storage.
    const refreshPage = () => window.location.reload();

    try {
      // chrome.runtime.id is undefined when the content script is orphaned
      // (extension already reloaded/updated underneath this tab). sendMessage
      // would throw "Extension context invalidated" — just refresh instead.
      if (!chrome.runtime?.id) {
        refreshPage();
        return;
      }

      // Fallback: on a real update the background reloads WhatsApp tabs via
      // onInstalled('update'). If that never happens (no update actually
      // pending, tab reload failed), this orphaned-context timer still fires
      // and refreshes the page so the modal can't hang forever.
      setTimeout(refreshPage, 6000);

      chrome.runtime.sendMessage({ type: 'INSTALL_UPDATE' }, (response) => {
        if (chrome.runtime.lastError) {
          console.warn('[UpdateModal] Update request failed:', chrome.runtime.lastError.message);
          refreshPage();
          return;
        }
        console.log('[UpdateModal] Update installation triggered:', response);
        // Extension reloads now; the background refreshes this tab.
      });
    } catch (error) {
      console.warn('[UpdateModal] Extension context unavailable, refreshing page:', error);
      refreshPage();
    }
  };

  const handleLater = () => {
    dispatch(dismissUpdateModal());
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] gap-6">
        <DialogHeader className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
              <ArrowUpCircle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-xl">Update Available</DialogTitle>
              <DialogDescription className="mt-1">
                A new version of Blueticks is ready to install
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Version Info */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800">
            <div className="space-y-1">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Current Version
              </p>
              <Badge variant="outline" className="font-mono text-xs text-slate-900 dark:text-slate-900 dark:bg-slate-100">
                v{currentVersion}
              </Badge>
            </div>
            
            <div className="flex items-center text-slate-400">
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
            
            <div className="space-y-1 text-right">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                New Version
              </p>
              <Badge className="font-mono text-xs bg-blue-600 hover:bg-blue-700 text-white">
                v{newVersion}
              </Badge>
            </div>
          </div>

          {/* What happens when updating */}
          <div className="flex gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/30">
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="space-y-1 text-sm">
              <p className="font-medium text-amber-900 dark:text-amber-200">
                What happens when you update:
              </p>
              <ul className="space-y-1 text-amber-800 dark:text-amber-300 list-disc list-inside">
                <li>The extension will reload automatically</li>
                <li>WhatsApp Web tabs will refresh</li>
                <li>Your conversations won't be affected</li>
                <li>Takes only a few seconds</li>
              </ul>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleLater}
            disabled={isUpdating}
            className="flex-1 sm:flex-none"
          >
            Update Later
          </Button>
          <Button
            onClick={handleUpdateNow}
            disabled={isUpdating}
            className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isUpdating ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <ArrowUpCircle className="mr-2 h-4 w-4" />
                Update Now
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
